import { CommandGroup, CommandItem, ScrollArea } from '@ais-ui/ui-core';
import { useMemo } from 'react';
import { cn } from '@ais-ui/utils';
import { PiBoatThin } from 'react-icons/pi';
import {
  RecentSearch,
  useRecentSearches,
} from '../../../components/RecentSearchesProvider';
import { useLocalization } from '@ais-ui/locale-management';

interface RecentVesselSearchesProps {
  onRecentSearchClick: (search: RecentSearch) => void;
}

export const RecentVesselSearches = ({
  onRecentSearchClick,
}: RecentVesselSearchesProps) => {
  const { messages } = useLocalization();
  const { recentSearches } = useRecentSearches();

  const recentVesselSearches = useMemo(
    () => recentSearches.filter((s) => s.type === 'vessel'),
    [recentSearches],
  );

  // Calculate dynamic height based on number of items
  const itemHeight = 52; // Height per item in pixels
  const maxHeight = 256; // Maximum height
  const calculatedHeight = Math.min(
    recentVesselSearches.length * itemHeight,
    maxHeight,
  );

  if (recentVesselSearches.length === 0) {
    return (
      <div className="py-6 text-center text-sm">
        {messages.shipSearchMinChars}
      </div>
    );
  }

  return (
    <>
      <CommandGroup heading={messages.mapSearchRecentSearchesHeading} />
      <ScrollArea
        className="max-h-[256px]"
        style={{ height: `${calculatedHeight}px` }}
      >
        {recentVesselSearches.map((search) => (
          <CommandItem
            key={search.id}
            className="mr-4 py-2 hover:cursor-pointer hover:bg-transparent data-[selected=true]:bg-transparent group border-b border-border last:border-b-0"
            onSelect={() => onRecentSearchClick(search)}
          >
            <div className="flex flex-col space-y-1 w-full">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-1">
                  <PiBoatThin
                    className={cn(
                      'mr-2 size-4',
                      search.metadata.requestType === 'database' &&
                        'text-red-500',
                      search.metadata.requestType === 'ship-db' &&
                        'text-purple-500',
                      search.metadata.requestType === 'ais' && 'text-blue-600',
                      (!search.metadata.requestType ||
                        search.metadata.requestType === 'no-recent-ais') &&
                        'text-yellow-500',
                    )}
                  />
                  <span className="group-hover:underline">{search.name}</span>
                </div>
              </div>
              <div className="flex space-x-2 text-xs text-muted-foreground items-center">
                <div>MMSI: {search.metadata.mmsi || 'N/A'}</div>
                <div
                  className={cn(
                    'rounded-full h-1 w-1',
                    search.metadata.requestType === 'ais' && 'bg-blue-600',
                    (!search.metadata.requestType ||
                      search.metadata.requestType === 'no-recent-ais') &&
                      'bg-yellow-500',
                    search.metadata.requestType === 'database' && 'bg-red-500',
                    search.metadata.requestType === 'ship-db' &&
                      'bg-purple-500',
                  )}
                />
                <div>IMO: {search.metadata.imo || 'None'}</div>
              </div>
            </div>
          </CommandItem>
        ))}
      </ScrollArea>
    </>
  );
};
